/**
 * A delay typed the way people say it — « 1 day 2 hours », « 90 min », « 1h30 », « 3 jours » —
 * read into days, hours and minutes, and written back the same way.
 *
 * The unit words follow the relative-date grammar of the list filters (`parseRelativeDate`:
 * « last 7 days », « 2 weeks ago »): singular or plural, so the same « 3 days » means the
 * same thing in a filter and in a wait. This grammar adds what a delay needs and a date range
 * does not: hours, minutes, and their short forms.
 */

export type DurationParts = { days: number; hours: number; minutes: number };

const UNIT_MINUTES: { pattern: RegExp; minutes: number }[] = [
    { pattern: /^(w|wk|wks|weeks?|semaines?)$/, minutes: 7 * 24 * 60 },
    { pattern: /^(d|days?|j|jours?)$/, minutes: 24 * 60 },
    { pattern: /^(h|hr|hrs|hours?|heures?)$/, minutes: 60 },
    { pattern: /^(m|mn|min|mins|minutes?)$/, minutes: 1 }
];

export type DurationReading = { ok: true; parts: DurationParts } | { ok: false; error: string };

/** « 1 day 2 hours » → `{ days: 1, hours: 2, minutes: 0 }`; empty or « 0 » is no delay. */
export function parseDurationText(input: string): DurationReading {
    const text = input.trim().toLowerCase().replace(/,|\band\b|\bet\b/g, " ");
    if (text === "" || text === "0") return { ok: true, parts: { days: 0, hours: 0, minutes: 0 } };
    // « 1h30 » is one hour and thirty minutes, the way a clock is read.
    const clock = text.match(/^(\d+)\s*h\s*(\d{1,2})$/);
    if (clock) return { ok: true, parts: normalise(Number(clock[1]) * 60 + Number(clock[2])) };

    let total = 0;
    const tokens = /(\d+(?:[.,]\d+)?)\s*([a-zéè]+)/g;
    let consumed = "";
    for (const match of text.matchAll(tokens)) {
        const unit = UNIT_MINUTES.find(candidate => candidate.pattern.test(match[2]));
        if (!unit) return { ok: false, error: `“${match[2]}” is not a unit. Use days, hours or minutes.` };
        total += Number(match[1].replace(",", ".")) * unit.minutes;
        consumed += match[0];
    }
    if (consumed.replace(/\s/g, "") !== text.replace(/\s/g, "")) {
        return { ok: false, error: "Write a number and a unit, e.g. 1 day 2 hours, 90 min or 1h30." };
    }
    return { ok: true, parts: normalise(Math.round(total)) };
}

/** `{ days: 1, hours: 2 }` → « 1 day 2 hours »; nothing is « No delay ». */
export function formatDurationParts({ days, hours, minutes }: Partial<DurationParts>): string {
    const words = [plural(days ?? 0, "day"), plural(hours ?? 0, "hour"), plural(minutes ?? 0, "minute")].filter(Boolean);
    return words.length ? words.join(" ") : "No delay";
}

function normalise(totalMinutes: number): DurationParts {
    return { days: Math.floor(totalMinutes / 1440), hours: Math.floor((totalMinutes % 1440) / 60), minutes: totalMinutes % 60 };
}

function plural(count: number, unit: string): string | null {
    if (!count) return null;
    return `${count} ${unit}${count > 1 ? "s" : ""}`;
}
